import { css } from '@emotion/react';
import Autolinker from 'autolinker';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BsFillLightningChargeFill } from 'react-icons/bs';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { RichTextEditor, CategorySheet } from '@/pages/Schedule/components';
import clockIcon from '@/pages/Schedule/icons/clock.png';
import labelIcon from '@/pages/Schedule/icons/label.png';
import pinIcon from '@/pages/Schedule/icons/pin.png';
import whoIcon from '@/pages/Schedule/icons/who.png';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import type { DecoratedEntry } from '@/pages/Schedule/types';
import { getDateSummary } from '@/pages/Schedule/utils';
import { LabelType } from '@/types/gql.generated';
import { Box, Flex, Link, Modal, VStack } from '@/ui';
import { isValidUrl } from '@/utils/url';
import {
  DescriptionInput,
  EmojiSheet,
  EntryModalBody,
  EntryModalContent,
  EntryModalField,
  EntryModalForm,
  EntryModalHeader,
  EntryModalSidebar,
  LabelSheet,
} from '../components';
import { EntryModalRsvpBar } from '../components/EntryModalRsvpBar';
import { MessagesButton } from '../components/MessagesButton';
import { MoreMenu } from '../components/MoreMenu';
import { RsvpButton } from '../components/RsvpButton';
import { MODAL_WIDTH } from '../constants';
import { useFetchEntryMessages } from '../hooks';
import type { EntryModalResult } from '../types';

type Props = {
  onClose: (result: EntryModalResult, values: DecoratedEntry) => void;
  onSave: (entry: DecoratedEntry) => Promise<void>;
};

export const FeedEntryModal = ({ onSave, onClose }: Props) => {
  const { t } = useTranslation(['common', 'entryModal']);
  const { value: rsvpEnabled } = useFeatureFlag('rsvpEnabled');

  const { draftTouched, entry, instance, updateDraftEntry } =
    useDraftEntryStore((state) => ({
      draftTouched: state.draftTouched,
      entry: state.draftEntry!,
      instance: state.draftInstance!,
      updateDraftEntry: state.updateDraftEntry,
      cloneDraftEntry: state.cloneDraftEntry,
    }));

  const { isLoading: isLoadingExistingMessages, messagesForInstance } =
    useFetchEntryMessages();

  // Sometimes multiple properties are updated in the same render cycle, so
  // storing the entry in a ref ensures we're operating on the latest value
  const entryRef = useRef<DecoratedEntry>(entry);
  useEffect(() => {
    entryRef.current = entry;
  }, [entry]);

  const updateEntry = (props: Partial<DecoratedEntry>) => {
    const newEntry: DecoratedEntry = {
      ...entryRef.current,
      ...props,
    };
    entryRef.current = newEntry;
    updateDraftEntry(newEntry);
  };

  const closeModal = (result: EntryModalResult = 'cancel') => {
    onClose(result, entry);
  };

  const handleRequestClose = () => {
    if (draftTouched) {
      closeModal('save');
    } else {
      closeModal('cancel');
    }
  };

  return (
    <Modal isCentered isOpen onClose={handleRequestClose}>
      <EntryModalContent data-testid="feed-entry-modal">
        <Flex direction="column" justify="space-between" w={MODAL_WIDTH + 'px'}>
          <EntryModalForm onSubmit={() => closeModal('save')}>
            <EntryModalHeader
              onCancelClick={() => closeModal('cancel')}
              onSaveClick={() => closeModal('save')}
            >
              {rsvpEnabled && <RsvpButton />}
              <MessagesButton
                hasMessages={messagesForInstance.length > 0}
                isDisabled={isLoadingExistingMessages}
              />
              <MoreMenu entry={entry} instance={instance} />
            </EntryModalHeader>

            <EntryModalBody>
              <EmojiSheet
                ml="-12px"
                mt="-12px"
                value={entry.emoji}
                onSelect={(emoji) => updateEntry({ emoji })}
              />

              <Box fontSize="2xl" mb="6">
                {instance?.title || t('common:untitled_entry')}
              </Box>

              <Flex
                align="center"
                bg="gray.100"
                borderRadius="md"
                fontSize="sm"
                mb="6"
                p="2"
                w="100%"
              >
                <Box>
                  <BsFillLightningChargeFill />
                </Box>
                <Box ml="3" wordBreak="break-all">
                  {t('entryModal:feed.imported_from', {
                    feed: entry.feed?.title,
                  })}
                </Box>
              </Flex>

              <VStack alignItems="start" mb="8" spacing="2">
                <EntryModalField
                  icon={
                    <img alt="" height="18px" src={clockIcon} width="18px" />
                  }
                >
                  <Box
                    css={css`
                      &:first-letter {
                        text-transform: capitalize;
                      }
                    `}
                  >
                    {getDateSummary(
                      instance.startDate,
                      instance.endDate,
                      instance.isOnDay,
                      instance.rule
                    )}
                  </Box>
                </EntryModalField>

                <CategorySheet
                  selected={entry.category}
                  onSelect={(category) => updateEntry({ category })}
                />

                <LabelSheet
                  icon={<img alt="" height="18px" src={whoIcon} width="18px" />}
                  labelType={LabelType.Who}
                  labels={entry.whoLabels}
                  placeholder={t('entryModal:who.placeholder')}
                  onChange={(labels) => updateEntry({ whoLabels: labels })}
                />

                <LabelSheet
                  labelType={LabelType.Default}
                  labels={entry.labels}
                  placeholder={t('entryModal:label.placeholder')}
                  icon={
                    <img alt="" height="18px" src={labelIcon} width="18px" />
                  }
                  onChange={(labels) => updateEntry({ labels })}
                />

                {instance?.locationWithPlace?.name && (
                  <EntryModalField
                    icon={
                      <img alt="" height="18px" src={pinIcon} width="18px" />
                    }
                  >
                    <Box>
                      <Link
                        target="_blank"
                        href={
                          isValidUrl(instance.locationWithPlace.name)
                            ? instance.locationWithPlace.name
                            : 'https://maps.google.com/maps?q=' +
                              encodeURIComponent(
                                instance.locationWithPlace.name
                              )
                        }
                      >
                        {instance.locationWithPlace.name}
                      </Link>
                    </Box>
                  </EntryModalField>
                )}
              </VStack>

              <Flex direction="column" flex="1">
                {instance?.description && (
                  <Flex mb="8">
                    <RichTextEditor
                      readOnly
                      value={Autolinker.link(instance.description)}
                    />
                  </Flex>
                )}
                <DescriptionInput
                  placeholder={t('entryModal:feed.notes_placeholder')}
                  style={{ minHeight: 125 }}
                  value={entry.notes ?? ''}
                  onChange={(notes) => updateEntry({ notes })}
                />
              </Flex>
            </EntryModalBody>
          </EntryModalForm>

          <EntryModalRsvpBar />
        </Flex>

        <EntryModalSidebar
          messages={messagesForInstance}
          onSave={() => onSave(entry)}
        />
      </EntryModalContent>
    </Modal>
  );
};
